import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { buildCacheKey, isCacheableRequest, type CacheKind } from './policy';
import { ANON_NS, getCacheNamespace } from './namespace';
import { enforceSizeCap, readCache, writeCache } from './httpCache';

/** Present on a response that came from disk rather than the network. */
export const STALE_HEADER = 'x-teamlio-cache';

type StaleListener = (stale: boolean) => void;
const listeners = new Set<StaleListener>();

export function onCacheStale(cb: StaleListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function emitStale(stale: boolean): void {
  listeners.forEach((cb) => {
    try {
      cb(stale);
    } catch {
      /* one bad subscriber must not break the interceptor */
    }
  });
}

/** A full store scan is expensive; amortise it over many writes. */
const CAP_CHECK_EVERY = 50;
let writesSinceCapCheck = 0;

function keyFor(config: InternalAxiosRequestConfig, kind: CacheKind, ns: string): string | null {
  if (!config.url) return null;
  return buildCacheKey({
    ns,
    kind,
    url: config.url,
    params: config.params as Record<string, unknown> | undefined,
  });
}

async function cacheResponse(response: AxiosResponse, kind: CacheKind): Promise<void> {
  const headers = response.headers as Record<string, unknown> | undefined;
  if (headers?.[STALE_HEADER] === 'stale') return; // never re-store what we just replayed
  if (response.status < 200 || response.status >= 300) return;

  const config = response.config as InternalAxiosRequestConfig;
  if (!isCacheableRequest(config.method, config.url)) return;

  const ns = getCacheNamespace();
  if (ns === ANON_NS) return; // nothing before login belongs on disk

  const key = keyFor(config, kind, ns);
  if (!key) return;

  await writeCache({ key, ns, status: response.status, data: response.data });

  writesSinceCapCheck += 1;
  if (writesSinceCapCheck >= CAP_CHECK_EVERY) {
    writesSinceCapCheck = 0;
    await enforceSizeCap();
  }
}

async function serveStale(error: any, kind: CacheKind): Promise<AxiosResponse | null> {
  const config = error?.config as InternalAxiosRequestConfig | undefined;
  if (!config) return null;

  // A 4xx is the server's authoritative answer — including 401/403, where
  // replaying cached data would keep showing content the user may have lost
  // access to. Only a dead network (no response) or a 5xx falls back.
  const status: number | undefined = error?.response?.status;
  if (status !== undefined && status < 500) return null;

  if (!isCacheableRequest(config.method, config.url)) return null;

  const ns = getCacheNamespace();
  if (ns === ANON_NS) return null;

  const key = keyFor(config, kind, ns);
  if (!key) return null;

  const entry = await readCache(key);
  if (!entry) return null;

  emitStale(true);
  return {
    data: entry.data,
    status: entry.status,
    statusText: 'OK (cached)',
    headers: { [STALE_HEADER]: 'stale' },
    config,
  } as unknown as AxiosResponse;
}

/**
 * Installs the read-through cache on an axios instance. Attach AFTER the
 * instance's own auth/refresh interceptor so a refreshable 401 is retried
 * before this layer ever sees it.
 */
export function attachCache(instance: AxiosInstance, kind: CacheKind): void {
  instance.interceptors.response.use(
    (response) => {
      const headers = response.headers as Record<string, unknown> | undefined;
      if (headers?.[STALE_HEADER] !== 'stale') emitStale(false);
      void cacheResponse(response, kind);
      return response;
    },
    async (error) => {
      const cached = await serveStale(error, kind);
      if (cached) return cached;
      return Promise.reject(error);
    },
  );
}
